import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CalendarDays, ChevronRight, Clock, PhoneCall } from "lucide-react";
import BLOGS from "@/data/blogs.json";
import { BLOG_UPDATED_LABEL, blogJsonLd, blogMeta, blogSummary, blogTitle, readMinutes } from "@/data/blogSeo";
import { CategoryFaqs } from "@/components/catalog/CategorySections";
import { findProduct } from "@/components/enquiry/ProductLines";
import { IMAGES } from "@/data/site";
import { SITE_URL, useSeo } from "@/lib/seo";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Block = ({ kind, t }) => {
  if (kind === "p" && t.endsWith("?") && t.length < 70) return <h3 className="mt-6 text-lg font-semibold text-slate-900" style={{ fontFamily: "Inter, sans-serif" }}>{t}</h3>;
  const lead = kind === "p" && t.match(/^([A-Z][A-Za-z0-9-]*(?: [A-Za-z0-9-]+){0,3}[.:])\s(.+)$/);
  return lead ? <p><strong className="text-slate-900">{lead[1]}</strong> {lead[2]}</p> : <p>{t}</p>;
};

const Section = ({ s }) => {
  const groups = s.blocks.reduce((acc, [kind, t]) => {
    const last = acc[acc.length - 1];
    if (kind === "li" && last?.kind === "li") last.items.push(t);
    else acc.push(kind === "li" ? { kind, items: [t] } : { kind, t });
    return acc;
  }, []);
  return (
    <section id={slug(s.h)} className="scroll-mt-24">
      {s.h && <h2 className="mt-12 text-2xl text-slate-900 sm:text-3xl">{s.h}</h2>}
      <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-slate-700">
        {groups.map((g, i) => (g.kind === "li" ? <ul key={i} className={g.items.every((it) => /^\d+\.\s/.test(it)) ? "space-y-2" : "list-disc space-y-2 pl-6"}>{g.items.map((it) => <li key={it}>{it}</li>)}</ul> : <Block key={i} kind={g.kind} t={g.t} />))}
      </div>
    </section>
  );
};

const Toc = ({ b }) => (
  <nav aria-label="In this guide" className="card-base p-5 text-sm" data-testid="blog-toc">
    <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-400">In this guide</p>
    <ol className="mt-3 space-y-2">{b.sections.filter((s) => s.h).map((s) => <li key={s.h}><a href={`#${slug(s.h)}`} className="text-slate-700 hover:text-[#C20500]">{s.h}</a></li>)}</ol>
  </nav>
);

const Products = ({ ids }) => (
  <section className="mt-14" data-testid="blog-related-products">
    <h2 className="text-2xl text-slate-900">Products mentioned in this guide</h2>
    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      {ids.map(findProduct).filter(Boolean).map(({ p, c }) => (
        <Link key={p.id} to={`/products/${p.id}`} className="card-base group flex items-center gap-4 p-4" data-testid={`blog-product-${p.id}`}>
          <img src={p.image} alt={p.name} loading="lazy" className="h-16 w-16 shrink-0 rounded-lg bg-white object-contain mix-blend-multiply" />
          <div className="min-w-0"><p className="font-semibold text-slate-900 group-hover:text-[#C20500]">{p.name}</p><p className="text-xs text-slate-500">{c.name} · {p.tagline}</p></div>
        </Link>
      ))}
    </div>
  </section>
);

const Cta = () => (
  <aside className="mt-14 rounded-2xl bg-[#123634] p-8 text-white" data-testid="blog-cta">
    <p className="text-2xl" style={{ fontFamily: "Fraunces, serif" }}>Need fire safety for your business?</p>
    <p className="mt-3 text-slate-300">Vinsafe supplies ISI-certified fire extinguishers, fire alarm and detection systems, evacuation equipment and fire safety AMCs for offices, hospitals, hotels, factories, warehouses and multi-site companies across India.</p>
    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
      <Link to="/enquiry" className="btn-primary px-6 py-3" data-testid="blog-cta-quote">Book a free site survey <ArrowRight className="h-4 w-4" /></Link>
      <a href="tel:+919654399019" className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-3 text-sm font-semibold" data-testid="blog-cta-call"><PhoneCall className="h-4 w-4" /> +91 96543 99019</a>
    </div>
  </aside>
);

const MoreBlogs = ({ b }) => {
  const i = BLOGS.findIndex((x) => x.slug === b.slug);
  const more = [1, 2, 3].map((k) => BLOGS[(i + k) % BLOGS.length]);
  return (
    <section className="border-t border-slate-200 bg-[#F4F6F2] py-14" data-testid="blog-more">
      <div className="container-x">
        <p className="eyebrow">Keep reading</p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {more.map((m) => <Link key={m.slug} to={`/blog/${m.slug}`} className="card-base p-5 font-semibold text-slate-900 hover:text-[#C20500]" data-testid={`blog-more-${m.slug}`}>{m.title}</Link>)}
        </div>
      </div>
    </section>
  );
};

const Article = ({ b }) => {
  const image = findProduct(b.related[0])?.p.image || IMAGES.hero;
  useSeo({ title: blogTitle(b), description: blogMeta(b), path: `/blog/${b.slug}`, image, jsonLd: blogJsonLd(SITE_URL, b, image) });
  return (
    <article data-testid={`blog-post-${b.slug}`}>
      <header className="container-x pt-10">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="blog-breadcrumb">
          <Link to="/" className="hover:text-slate-900">Home</Link><ChevronRight className="h-3 w-3" /><Link to="/blog" className="hover:text-slate-900">Blog</Link>
        </nav>
        <h1 className="mt-6 max-w-4xl text-4xl leading-[1.1] text-slate-900 sm:text-5xl" data-testid="blog-title">{b.title}</h1>
        <p className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> Updated: {BLOG_UPDATED_LABEL}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {readMinutes(b)} min read</span>
          <span>By the Vinsafe fire safety team</span>
        </p>
      </header>
      <div className="container-x grid gap-12 pb-16 pt-8 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0 max-w-3xl">
          <p className="rounded-xl border-l-4 border-[#C20500] bg-[#FBEDEA]/60 p-5 text-[17px] leading-relaxed text-slate-800" data-testid="blog-summary">{blogSummary(b)}</p>
          <div className="mt-8 space-y-4 text-[17px] leading-relaxed text-slate-700" data-testid="blog-intro">{b.intro.map(([, t], i) => <p key={i}>{t}</p>)}</div>
          {b.sections.map((s, i) => <Section key={i} s={s} />)}
          <Products ids={b.related} />
          <Cta />
        </div>
        <div className="hidden lg:block"><div className="sticky top-24"><Toc b={b} /></div></div>
      </div>
      {b.faqs.length > 0 && <CategoryFaqs faqs={b.faqs} />}
      <MoreBlogs b={b} />
    </article>
  );
};

export default function BlogPost() {
  const { slug: s } = useParams();
  const b = BLOGS.find((x) => x.slug === s);
  if (!b) return <Navigate to="/blog" replace />;
  return <Article key={b.slug} b={b} />;
}
